import React, { useState, useEffect } from 'react';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import LinearProgress from '@mui/material/LinearProgress';
import ErrorIcon from '@mui/icons-material/Error';
import MenuItem from '@mui/material/MenuItem';
import { IApplicationDocumentType } from '../Interfaces/Application/IApplicationDocumentType';
import Menu from '@mui/material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDispatch } from 'react-redux';
import { ApplicationDispatch } from '../store/application/types';
import GetAppIcon from '@mui/icons-material/GetApp';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useMutateApplicationDocument, Document, downloadFile } from '../Data/ApplicationDocumentData';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import Checkbox from '@mui/material/Checkbox';
import { ListItemAvatar, ListItemButton } from '@mui/material';
import { useEmployeeRolePermissions } from "../Data/EmployeeRoleData";
import { isNotNil } from 'ramda';

const validationSchema = yup.object({
  name: yup.string().required().max(150),
  applicationDocumentTypeIds: yup.array().of(yup.number()),
  notes: yup.string().max(150),
})

export interface ApplicationDocumentProps {
  applicationId: string;
  document: Document;
  applicationDocumentTypes: IApplicationDocumentType[];
  isEditing?: boolean;
  isSelected: boolean;
  isReadonly?: boolean;
  isCheckable?: boolean;
  isChecked?: boolean;
  onChecked?: (id: string) => void;
  onSelected: (id: string) => void;
  onDelete: (id: number) => void;
  onSave?: () => void;
}

export const ApplicationDocument: React.FC<ApplicationDocumentProps> = (props) => {
  const [canView, canEdit, canAct] = useEmployeeRolePermissions();
  const dispatch = useDispatch<ApplicationDispatch>();
  const [isEditing, setIsEditing] = useState(false);
  const [updateApplicationDocument] = useMutateApplicationDocument() as any;
  const d = props.document;

  useEffect(() => {
    if (props.isEditing) {
      setIsEditing(true);
    }
  }, [props.isEditing]);

  let typeNames: string[] = [];
  for (const id of (d.applicationDocumentTypeIds || [])) {
    const typeName = props.applicationDocumentTypes.find(t => t.id === id)?.name;
    if (typeName) {
      typeNames.push(typeName);
    }
  }
  let applicationDocumentType = typeNames.join(", ");
  if (d.notes) {
    applicationDocumentType = `${applicationDocumentType} - ${d.notes}`;
  }

  const createdDate = d.createdDate && `${new Date(d.createdDate).toLocaleDateString([], { hour: '2-digit', minute: '2-digit' })}`;

  let secondaryDisplayText = `Uploaded ${createdDate ?? ''}`;
  if (d.source) {
    secondaryDisplayText += ` • Source: ${d.source}`;
  }
  if (d.notes && d.notes?.length > 0) {
    secondaryDisplayText += `, Notes: ${d.notes}`;
  }

  // Dropdown
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { register, control, errors, watch, reset } = useForm<DocumentEdit>({
    validationSchema: validationSchema,
    mode: 'onChange',
    reValidateMode: "onChange",
    defaultValues: {
      name: d.name,
      applicationDocumentTypeIds: d.applicationDocumentTypeIds || [],
      notes: d.notes,
      allowCustomerToViewInPortal: d.allowCustomerToViewInPortal || false,
    },
  });
  const latestValues = watch();

  const editForm = (
    <div onClick={(e) => e.stopPropagation()}>
      <TextField
        name="name"
        inputRef={register}
        label="Name"
        size="small"
        defaultValue={d.name}
        required
        multiline
        fullWidth
        variant="outlined"
        error={!!errors.name}
        helperText={errors.name?.message}
        margin="dense"
        onClick={(e) => e.stopPropagation()}
      />
      <TextField
        name="notes"
        inputRef={register}
        size="small"
        defaultValue={d.notes}
        label="Notes"
        multiline
        fullWidth
        variant="outlined"
        error={!!errors.notes}
        helperText={errors.notes?.message}
        margin="dense"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );

  return (
    <React.Fragment>
      <ListItemButton
        selected={props.isSelected}
        onClick={() => props.onSelected(d.documentId)}
        style={isEditing ? { background: props.isSelected ? '#bddefb' : 'inherit', cursor: 'default' } : { background: props.isSelected ? '#bddefb' : 'inherit', cursor: 'pointer' }}
      >
        {props.isCheckable ?
          <ListItemAvatar>
            <Checkbox
              checked={props.isChecked}
              onClickCapture={(e) => {
                e.nativeEvent?.stopImmediatePropagation?.();
                e.stopPropagation();
                e.preventDefault();
                props.onChecked?.(d.documentId);
              }}
              edge="start"
            />
          </ListItemAvatar>
          : null
        }
        <ListItemText primary={isEditing ? editForm : d.name} secondary={d.applicationDocumentTypeIds && d.applicationDocumentTypeIds.length > 0 ? applicationDocumentType : secondaryDisplayText} />
        {d.file &&
          <ListItemText>
            <LinearProgress />
          </ListItemText>
        }
        {d.error &&
          <ListItemText inset primary={<ErrorIcon color="secondary" />} secondary={d.error} />
        }
        {!props.isReadonly &&
          <ListItemSecondaryAction>
            {isEditing ? (
              <div>
                <div>
                  <IconButton
                    edge="end"
                    aria-label="save"
                    aria-controls="long-menu"
                    aria-haspopup="true"
                    onClick={async () => {
                      await updateApplicationDocument({
                        applicationId: props.applicationId,
                        documentId: d.documentId,
                        name: latestValues.name,
                        applicationDocumentTypeIds: latestValues.applicationDocumentTypeIds || [],
                        notes: latestValues.notes,
                        allowCustomerToViewInPortal: latestValues.allowCustomerToViewInPortal,
                      });
                      d.name = latestValues.name;
                      d.applicationDocumentTypeIds = latestValues.applicationDocumentTypeIds || [];
                      d.notes = latestValues.notes;
                      d.isNew = undefined;
                      d.allowCustomerToViewInPortal = latestValues.allowCustomerToViewInPortal;
                      reset();
                      setIsEditing(false);
                    }}>
                    <SaveIcon />
                  </IconButton>
                </div>
                <div>
                  <IconButton
                    edge="end"
                    aria-label="cancel"
                    aria-controls="long-menu"
                    aria-haspopup="true"
                    onClick={() => {
                      reset();
                      setIsEditing(false);
                    }}>
                    <CancelIcon />
                  </IconButton>
                </div>
              </div>
            ) : (
              <IconButton
                edge="end"
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                onClick={handleClick}>
                <MoreVertIcon />
              </IconButton>
            )}
          </ListItemSecondaryAction>
        }
      </ListItemButton>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}>
        <MenuItem aria-label="edit"
          onClick={() => {
            reset({
              name: d.name,
              applicationDocumentTypeIds: d.applicationDocumentTypeIds,
              notes: d.notes,
            })
            setIsEditing(true);
            handleClose();
          }}>
          <EditIcon />
          Edit
        </MenuItem>
        <MenuItem aria-label="download"
          onClick={() => {
            downloadFile(d.documentId, d.name, dispatch);
            handleClose();
          }}>
          <GetAppIcon />
          Download
        </MenuItem>
        {isNotNil(d.documentId) && (
          <MenuItem
            aria-label="view-in-customer-portal"
            onClick={async () => {
              // update the document
              await updateApplicationDocument({
                applicationId: props.applicationId,
                documentId: d.documentId,
                name: latestValues.name,
                applicationDocumentTypeIds: latestValues.applicationDocumentTypeIds || [],
                notes: latestValues.notes,
                allowCustomerToViewInPortal: !latestValues.allowCustomerToViewInPortal,
              });
              d.name = latestValues.name;
              d.applicationDocumentTypeIds = latestValues.applicationDocumentTypeIds || [];
              d.notes = latestValues.notes;
              d.isNew = undefined;
              d.allowCustomerToViewInPortal = !latestValues.allowCustomerToViewInPortal;
              reset();
            }}
          >
            <Checkbox
              checked={d.allowCustomerToViewInPortal === true}
              size="small"
              edge="start"
              style={{ paddingTop: 0, paddingBottom: 0 }}
            />
            View In Customer Portal?
          </MenuItem>
        )}
        {canAct("Actions", "Delete Document") &&
          <MenuItem
            aria-label="delete"
            onClick={() => {
              setIsDialogOpen(true);
              handleClose();
            }}>
            <DeleteIcon />
            Delete
          </MenuItem>
        }
      </Menu>
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete document?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You can recover the deleted file for short amount of time in case you change your mind.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} color="inherit" autoFocus>
            Cancel
          </Button>
          <Button onClick={() => { props.onDelete(d.id); setIsDialogOpen(false); }} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

interface DocumentEdit {
  name: string;
  applicationDocumentTypeIds: number[];
  notes: string;
  allowCustomerToViewInPortal: boolean;
}
